import { zodResolver } from '@hookform/resolvers/zod';
import { LoginInput } from '@uit/shared/schemas/auth';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import { LegalFooter } from '../components/LegalFooter.js';
import { useAuth } from '../lib/auth.js';
import s from './LoginPage.module.scss';

export function LoginPage() {
  const { login } = useAuth();
  const location = useLocation();
  const passwordResetFlash = (location.state as { passwordReset?: boolean } | null)?.passwordReset;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginInput),
  });

  const onSubmit = async (data: LoginInput): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await login(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // The apiMutation throws on non-2xx; the message is "<code> <statusText>: <body>".
      setSubmitError(
        message.includes('401')
          ? 'Invalid email or password.'
          : 'Sign-in failed. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={s.page}>
      <form
        className={s.card}
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <h1 className={s.title}>Sign in</h1>
        <p className={s.subtitle}>ultimumIT portal — admin access</p>

        {passwordResetFlash && !submitError && (
          <div
            role="status"
            style={{
              background: '#ecfdf5',
              color: '#16a34a',
              border: '1px solid #16a34a',
              borderRadius: 6,
              padding: '8px 12px',
              fontSize: 14,
              marginBottom: 16,
            }}
          >
            Password reset. Sign in with your new password.
          </div>
        )}

        {submitError && (
          <div className={s.formError} role="alert">
            {submitError}
          </div>
        )}

        <div className={s.field}>
          <label className={s.label} htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className={s.input}
            aria-invalid={!!errors.email}
            {...register('email')}
          />
          {errors.email && <div className={s.fieldError}>{errors.email.message}</div>}
        </div>

        <div className={s.field}>
          <label className={s.label} htmlFor="password">
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            className={s.input}
            aria-invalid={!!errors.password}
            {...register('password')}
          />
          {errors.password && <div className={s.fieldError}>{errors.password.message}</div>}
        </div>

        <button type="submit" className={s.button} disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
        <p style={{ marginTop: 12, fontSize: 13, textAlign: 'center' }}>
          <Link to="/forgot-password">Forgot password?</Link>
        </p>
      </form>
      <LegalFooter />
    </div>
  );
}
